import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import DashboardList from '../components/Dashboards/DashboardList';
import DashboardContent from '../components/Dashboards/DashboardContent';
import DashboardModals from '../components/Dashboards/DashboardModals';
import QuerySection from '../components/Dashboards/QuerySection';
import DashboardManager from '../components/Dashboards/DashboardManager';
import { useConfig } from '../contexts/ConfigContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useQueryLibraryCore } from '../hooks/useQueryLibraryCore';


const Home = ({ mainContentRef }) => {
  const [dashboards, setDashboards] = useState([]);
  const [isNewDashboardModalOpen, setIsNewDashboardModalOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [queryResult, setQueryResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser, isAdmin } = useAuth();
  const { selectedDashboard, updateSelectedDashboard } = useDashboard();
  const savedQueriesRef = useRef();
  const [isAddToDashboardModalOpen, setIsAddToDashboardModalOpen] = useState(false);
  const [queryToAdd, setQueryToAdd] = useState(null);
  const [isEditDashboardModalOpen, setIsEditDashboardModalOpen] = useState(false);
  const [availableQueries, setAvailableQueries] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);
  const [isQueryJustAdded, setIsQueryJustAdded] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [isShared, setIsShared] = useState(false);
  
  // Get saveQueryTitle from the hook
  const { saveQueryTitle } = useQueryLibraryCore();

  const REFRESH_INTERVAL = 3600000; // 1 hour in milliseconds (60 * 60 * 1000)
  const ACTIVITY_THRESHOLD = 3600000; // 1 hour in milliseconds (60 * 60 * 1000)

  const { config, isLoading: isConfigLoading } = useConfig();
  
  const handleQueryResult = useCallback((result) => {
    setQueryResult(result);
    setIsLoading(false);
  }, []);

  const handleQueryStart = useCallback(() => {
    setIsLoading(true);
    setQueryResult(null);
  }, []);

  const submitQuery = useCallback(
    async (query, isFollowUp = false, highlightedText = '', originalSqlQuery = '', selectedColumns = [], context = {}) => {
      handleQueryStart();
      try {
        const userId = currentUser?.email || 'anonymous';
        const userQuery = query;
  
        const response = await fetch(
          'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/processDynamicQueryWithCORS',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: {
                userQuery: userQuery,
                userId: userId,
                isFollowUp: isFollowUp,
                isAdmin: isAdmin,
                highlightedText: highlightedText,
                originalSqlQuery: originalSqlQuery,
                originalDocumentId: queryResult ? queryResult.documentId : null,
                originalSavedQueryId: queryResult ? queryResult.savedQueryId : null,
                selectedColumns: selectedColumns,
                valueContext: context?.valueContext || null,
                isFromValueSearchList: context?.isFromValueSearchList || false
              },
              context: { auth: true },
            }),
          }
        );
  
        const data = await response.json();
  
        if (!response.ok) {
          throw new Error('Failed to process query.');
        }
  
        const {
          result = [],
          sqlQuery = '',
          schema = '',
          userQuery: responseUserQuery = query,
          truncationMessage = '',
          truncated = false,
          explanation = '',
          isFollowUp: responseIsFollowUp = isFollowUp,
          originalQuery = '',
          documentId = queryResult ? queryResult.documentId : null,
          savedQueryId = queryResult ? queryResult.savedQueryId : null,
          retryCount = 0,
          retryResult = 'N/A',
          bigQueryError = null,
          status = 'completed',
          endTime = new Date().toISOString(),
        } = data;
  
        // If it's a follow-up query and no explanation was provided, generate one
        let finalExplanation = explanation;
        if (isFollowUp && !explanation) {
          try {
            const explanationResponse = await fetch(
              'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/generateExplanation',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  data: {
                    sqlQuery: sqlQuery,
                    documentId: documentId,
                    savedQueryId: savedQueryId,
                  },
                }),
              }
            );
            if (explanationResponse.ok) {
              const explanationData = await explanationResponse.json();
              finalExplanation = explanationData.explanation;
            }
          } catch (error) {
          }
        }
  
        const newResult = {
          result,
          sqlQuery,
          schema,
          userQuery: responseUserQuery,
          truncationMessage,
          truncated,
          explanation: finalExplanation,
          isFollowUp: responseIsFollowUp,
          originalQuery,
          documentId,
          savedQueryId,
          config: {
            usedModel: data.usedModel || 'Unknown',
            fallbackUsed: data.fallbackUsed || false,
          },
          retryCount,
          retryResult,
          bigQueryError,
          status,
          endTime,
        };
  
        handleQueryResult(newResult);
  
        setIsLoading(false);
  
        setTimeout(() => {
          if (mainContentRef && mainContentRef.current) {
            mainContentRef.current.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }, 100);
      } catch (error) {
        setError({
          message: 'An unexpected error occurred. Please try again.',
          status: 'error',
        });
        setIsLoading(false);
        handleQueryResult(null);
      }
    },
    [currentUser, handleQueryStart, handleQueryResult, queryResult, mainContentRef, setIsLoading, setError, isAdmin, config.core.projectId]
  );

  const handleFollowUpQuery = useCallback(
    (followUpQuery, highlightedText, originalSqlQuery, selectedColumns, context = {}) => {
      submitQuery(
        followUpQuery, 
        true, 
        highlightedText, 
        originalSqlQuery, 
        selectedColumns,
        context
      );

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      if (mainContentRef && mainContentRef.current) {
        mainContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    },
    [submitQuery, mainContentRef]
  );

  const dashboardManager = DashboardManager({
    dashboards,
    setDashboards,
    setError,
    handleQueryResult,
    setIsDashboardLoading,
    currentUser,
    isAdmin,
    saveQueryTitle,
  });

  const handleRefreshDashboard = useCallback(async (dashboard) => {
    if (dashboard) {
      try {
  
        await dashboardManager.handleRefreshDashboard(dashboard);
        
      } catch (error) {
        setError('Failed to refresh dashboard. Please try again.');
      }
    }
  }, [dashboardManager, setError, selectedDashboard]);

  useEffect(() => {
    if (currentUser !== null) {
      setAuthChecked(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!selectedDashboard || !autoRefreshEnabled) return;
  
    let refreshInterval;
    const SESSION_KEY = 'tangoLastActive';
    const ACTIVITY_WINDOW = 3600000; // 1 hour in milliseconds
  
    const updateLastActive = () => {
      sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    };
  
    const isActiveWithinWindow = () => {
      const lastActive = sessionStorage.getItem(SESSION_KEY);
      if (!lastActive) return false;
      
      const timeSinceActive = Date.now() - parseInt(lastActive);
      return timeSinceActive < ACTIVITY_WINDOW;
    };
  
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(refreshInterval);
      } else {

        updateLastActive();
        
        if (isActiveWithinWindow()) {
          clearInterval(refreshInterval);
          
          // Check if immediate refresh needed
          const lastRefreshTime = selectedDashboard.lastRefreshed?._seconds 
            ? selectedDashboard.lastRefreshed._seconds * 1000
            : 0;
          
          const timeSinceLastRefresh = Date.now() - lastRefreshTime;
          if (timeSinceLastRefresh >= REFRESH_INTERVAL) {

            handleRefreshDashboard(selectedDashboard);
          }
  
          // Set up new refresh cycle
          refreshInterval = setInterval(() => {
            if (isActiveWithinWindow()) {

              handleRefreshDashboard(selectedDashboard);
            }
          }, REFRESH_INTERVAL);
        }
      }
    };
  
    // Set up event listeners
    window.addEventListener('focus', updateLastActive);
    window.addEventListener('blur', updateLastActive);
    window.addEventListener('mousemove', updateLastActive);
    window.addEventListener('keypress', updateLastActive);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  
    // Initial setup
    updateLastActive();
    handleVisibilityChange();
  
    return () => {
      window.removeEventListener('focus', updateLastActive);
      window.removeEventListener('blur', updateLastActive);
      window.removeEventListener('mousemove', updateLastActive);
      window.removeEventListener('keypress', updateLastActive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);

    };
  }, [selectedDashboard, autoRefreshEnabled, handleRefreshDashboard, REFRESH_INTERVAL]);

  const fetchAvailableQueries = useCallback(async () => {
    if (!currentUser) return;
    try {
      const getSavedQueries = httpsCallable(functions, 'getSavedQueries');
      const result = await getSavedQueries({
        userId: currentUser.email,
        isAdmin: isAdmin
      });
      
      // With httpsCallable, the response is in result.data
      const queries = result.data.queries || [];
      setAvailableQueries(queries);
    } catch (error) {
      console.error('Error fetching queries:', error);
      setError('Failed to fetch available queries. Please try again later.');
      setAvailableQueries([]);
    }
  }, [currentUser, isAdmin, setError]);

  useEffect(() => {
    fetchAvailableQueries();
  }, [fetchAvailableQueries]);

  const filterAvailableQueries = useCallback(
    (dashboardQueries) => {
      const dashboardQueryIds = dashboardQueries.map((q) => q.id);
      return availableQueries.filter((q) => !dashboardQueryIds.includes(q.id));
    },
    [availableQueries]
  );

  const handleNewDashboard = () => {
    if (!currentUser) {
      setError('Please log in to perform this action.');
      return;
    }
    setIsNewDashboardModalOpen(true);
  };

  const handleCreateDashboard = async () => {
    const newDashboard = await dashboardManager.handleCreateDashboard(
      newDashboardName,
      selectedQueries,
      isShared
    );
    if (newDashboard) {
      setIsNewDashboardModalOpen(false);
      setNewDashboardName('');
      setSelectedQueries([]);
      setIsShared(false);
      setDashboards((prevDashboards) => [...prevDashboards, newDashboard]);
      dashboardManager.handleSelectDashboard(newDashboard);
    }
  };

  const handleSaveQuery = useCallback(() => {
    if (savedQueriesRef.current) {
      savedQueriesRef.current.refreshQueries();
    }
    fetchAvailableQueries();
  }, [fetchAvailableQueries]);

  const handleExplanationClick = async (query) => {
    try {
      const response = await fetch(
        'https://us-central1-' + config.core.projectId + '.cloudfunctions.net/generateExplanation',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              sqlQuery: query.sqlQuery,
              savedQueryId: query.id,
            },
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to generate explanation.');
      }
      const data = await response.json();
      const updatedQueries = selectedDashboard.queries.map((q) =>
        q.id === query.id ? { ...q, explanation: data.explanation } : q
      );
      updateSelectedDashboard({ ...selectedDashboard, queries: updatedQueries });
    } catch (error) {
      setError('Failed to generate explanation. Please try again.');
    }
  };

  const handleSaveCardSettings = dashboardManager.handleSaveCardSettings;


  const handleAddToDashboard = useCallback((query) => {
    setQueryToAdd(query);
    setIsAddToDashboardModalOpen(true);
    setIsQueryJustAdded(false);
  }, []);

  const handleAddQueryToDashboard = async (dashboardId) => {
    const success = await dashboardManager.handleAddQueryToDashboard(
      dashboardId,
      queryToAdd
    );
    if (success) {
      setIsQueryJustAdded(true);
    }
  };

  const handleEditDashboard = (dashboard) => {
    updateSelectedDashboard(dashboard);
    setIsEditDashboardModalOpen(true);
  };

  const handleUpdateDashboard = async (updatedDashboard) => {
    const success = await dashboardManager.handleUpdateDashboard(updatedDashboard);
    if (success) {
      setIsEditDashboardModalOpen(false);
    }
  };

  const handleDashboardUpdate = useCallback(
    (updatedDashboard) => {
      if (!updatedDashboard || !updatedDashboard.id) {
        return;
      }
      dashboardManager.handleSelectDashboard(updatedDashboard);
      fetchAvailableQueries();
      setDashboards((prevDashboards) =>
        prevDashboards.map((d) =>
          d.id === updatedDashboard.id ? updatedDashboard : d
        )
      );
      if (selectedDashboard && selectedDashboard.id === updatedDashboard.id) {
        updateSelectedDashboard(updatedDashboard);
      }
    },
    [dashboardManager, fetchAvailableQueries, selectedDashboard, updateSelectedDashboard]
  );


  if (!authChecked) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-2 sm:py-4">
      {currentUser ? (
        <>
          <h1 className="text-gray-500 mb-6 text-xl font-semibold">Ask Tango</h1>
          <QuerySection
            onQueryResult={handleQueryResult}
            onQueryStart={handleQueryStart}
            queryResult={queryResult}
            isLoading={isLoading || isConfigLoading}
            currentUser={currentUser}
            onSaveQuery={handleSaveQuery}
            onAddToDashboard={handleAddToDashboard}
            onFollowUpQuery={handleFollowUpQuery}
            mainContentRef={mainContentRef}
            submitQuery={submitQuery}
            onDashboardUpdate={handleDashboardUpdate}
            dashboardManager={dashboardManager}
            isQueryJustAdded={isQueryJustAdded}
            setIsQueryJustAdded={setIsQueryJustAdded}
            isAskPage={true} // Set to true for "Ask Tango" section
            isDashboardMode={false} // Set to false for "Ask Tango" section
            hideProposedQuestions={true}
          />
          <div className="border-t border-gray-200 my-8"></div>
          <h1 className="text-gray-500 mb-6 text-xl font-semibold">Dashboards</h1>
          <div className="mt-8 space-y-4">
          <DashboardList
            dashboards={dashboards}
            selectedDashboard={selectedDashboard}
            onSelectDashboard={dashboardManager.handleSelectDashboard}
            onNewDashboard={handleNewDashboard}
            onEditDashboard={handleEditDashboard}
            onRefreshDashboard={handleRefreshDashboard}
            currentUser={currentUser}
            isAdmin={isAdmin}
            viewMode={dashboardManager.dashboardViewMode}
            onViewModeChange={dashboardManager.updateDashboardViewMode}
          />
            <DashboardContent
              error={error}
              isDashboardLoading={isDashboardLoading}
              selectedDashboard={selectedDashboard}
              currentUser={currentUser}
              onSaveCardSettings={handleSaveCardSettings}
              onFollowUpQuery={handleFollowUpQuery}
              onSaveQuery={handleSaveQuery}
              onExplanationClick={handleExplanationClick}
              isDashboardMode={true}
              isAdmin={isAdmin}
              viewMode={dashboardManager.dashboardViewMode}
              updateSelectedDashboard={updateSelectedDashboard}
              saveQueryTitle={dashboardManager.saveQueryTitle || saveQueryTitle}
            />
          </div>
          <DashboardModals
            isNewDashboardModalOpen={isNewDashboardModalOpen}
            setIsNewDashboardModalOpen={setIsNewDashboardModalOpen}
            newDashboardName={newDashboardName}
            setNewDashboardName={setNewDashboardName}
            selectedQueries={selectedQueries}
            setSelectedQueries={setSelectedQueries}
            handleCreateDashboard={handleCreateDashboard}
            currentUser={currentUser}
            availableQueries={availableQueries}
            isAddToDashboardModalOpen={isAddToDashboardModalOpen}
            setIsAddToDashboardModalOpen={setIsAddToDashboardModalOpen}
            dashboards={dashboards}
            handleAddQueryToDashboard={handleAddQueryToDashboard}
            handleNewDashboard={handleNewDashboard}
            isEditDashboardModalOpen={isEditDashboardModalOpen}
            setIsEditDashboardModalOpen={setIsEditDashboardModalOpen}
            selectedDashboard={selectedDashboard}
            handleUpdateDashboard={handleUpdateDashboard}
            handleDeleteDashboard={dashboardManager.handleDeleteDashboard}
            filterAvailableQueries={filterAvailableQueries}
            onDashboardUpdate={handleDashboardUpdate}
            isQueryJustAdded={isQueryJustAdded}
            setIsQueryJustAdded={setIsQueryJustAdded}
            isShared={isShared}
            setIsShared={setIsShared}
          />
        </>
      ) : (
        <div className="text-center py-10">
          <h2 className="text-2xl font-bold mb-4">Please log in to view this page</h2>
          <p>You need to be logged in to access the dashboard and query features.</p>
        </div>
      )}
    </div>
  );
};

export default Home;